/* eslint-disable max-lines */
import { ThemeProvider } from "@mui/material";
import { notEmpty, theme, useResponsive } from "@product/scmp-sdk";
import type { FunctionComponent, PartialPropsOf, ReactNode } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type { Required } from "utility-types";

import { TakeoverAdSlot } from "~/components/advertisement/takeover-ad-slot";
import { ArticleImageGallery } from "~/components/article/article-image-gallery";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import { usePaywallCheckerContext } from "~/components/article/article-paywall-checker/contexts";
import type { Props as ArticlePrintHeadlineMessageProps } from "~/components/article/article-print-headline-message";
import { ArticlePrintHeadlineMessage } from "~/components/article/article-print-headline-message";
import { checkIsPlusArticle } from "~/components/article/article-render/plus/helpers";
import { checkIsStyleArticle } from "~/components/article/article-render/style-article/helper";
import { ArticleSpeechSkipContainer } from "~/components/article/article-speech";
import { ArticleSpeechPlayerPortal } from "~/components/article/article-speech/article-speech-player-portal";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { ContentBody } from "~/components/content/content-body";
import { useContentReadMoreContext } from "~/components/content/content-read-more";
import { ContentReadMoreButton } from "~/components/content/content-read-more/content-read-more-button";
import { type Props as InlineAdSlotProps } from "~/components/schema-render/common/inline-ad-slot";
import type { InlineAdSlotRenderProps } from "~/components/schema-render/common/inline-ad-slot/hooks";
import { defineInlineAdSlotSchema } from "~/components/schema-render/common/inline-ad-slot/schemas";
import { article as articleData } from "~/data";
import { useArticleUtils } from "~/lib/hooks/article-utils";
import type { articleRenderArticle$key } from "~/queries/__generated__/articleRenderArticle.graphql";

import { NumberOfReadableElementInPaywall } from "./consts";
import { useArticleRenderContext } from "./contexts";
import {
  DynamicGenericArticle,
  DynamicInfographicArticle,
  DynamicLiveArticle,
  DynamicLongReadArticle,
  DynamicMorningStudioArticle,
  DynamicNewsletterArticle,
  DynamicOpinionArticle,
  DynamicPhotoEssayArticle,
  DynamicPlusColumnsArticle,
  DynamicPlusGenericArticle,
  DynamicSeriesArticle,
  DynamicStyleArticle,
} from "./dynamics";
import { GlobalChecker } from "./global-checker";
import { withArticleRenderContext } from "./hoc";
import { useMostLikelyToSubscribe, usePianoIntegration } from "./hooks";
import { useArticleBaseAdvertisingInfo } from "./hooks/article-base-advertising-info";
import { checkIsPostiesArticle } from "./posties/helpers";
import { usePostiesArticle } from "./posties/hooks";
import type { InlinePaywallOverlayerProps } from "./styles";
import {
  ArticleRenderContainer,
  ContentReadMoreButtonContainer,
  InlinePaywallOverlayer,
  PrimaryContentBody,
  SecondlyContentBody,
  StyledAdSlot,
  StyledArticleActionWidget,
  StyledArticleAdditionalInfoBox,
  StyledArticleAuthorBlock,
  StyledArticleAuthorsDetails,
  StyledArticleCommentsTrigger,
  StyledArticleCorrectionsWidget,
  StyledArticleLeading,
  StyledArticleLeftHandSideWidgets,
  StyledArticleLiveTag,
  StyledArticleMoreOnThisList,
  StyledArticlePaywallContainer,
  StyledArticleReaction,
  StyledArticleSections,
  StyledArticleShareWidget,
  StyledArticleSpeechInlineWidget,
  StyledArticleSponsor,
  StyledArticleTopicFollowWidget,
  StyledArticleTopics,
  StyledArticleVocabBox,
  StyledCommentAndReactionContainer,
  StyledContentHeadline,
  StyledContentSubHeadline,
} from "./styles";
import { theme as defaultTheme } from "./theme";
export * from "./types";

export type Props = {
  reference: articleRenderArticle$key;
};

export type DefaultComponentsProvider = {
  actionWidget: (props?: PartialPropsOf<typeof StyledArticleActionWidget>) => ReactNode;
  adslot: (props: Required<PartialPropsOf<typeof StyledAdSlot>, "sizes" | "adUnit">) => ReactNode;
  articleAdditionalInfoBox: (
    props?: PartialPropsOf<typeof StyledArticleAdditionalInfoBox>,
  ) => ReactNode;
  authorBlock: (props?: PartialPropsOf<typeof StyledArticleAuthorBlock>) => ReactNode;
  authorDetails: (props?: PartialPropsOf<typeof StyledArticleAuthorsDetails>) => ReactNode;
  body: (
    props?: PartialPropsOf<typeof ContentBody> & {
      isDisabledArticleImageGallery?: boolean;
      paywallVariant?: InlinePaywallOverlayerProps["$variant"];
      printHeadlineVariant?: ArticlePrintHeadlineMessageProps["variant"];
    },
  ) => ReactNode;
  commentTriggerAndReaction: (
    props?: PartialPropsOf<typeof StyledArticleCommentsTrigger>,
    reactionProps?: PartialPropsOf<typeof StyledArticleReaction>,
  ) => ReactNode;
  correction: (props?: PartialPropsOf<typeof StyledArticleCorrectionsWidget>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof StyledContentHeadline>) => ReactNode;
  leading: (props?: PartialPropsOf<typeof StyledArticleLeading>) => ReactNode;
  leftHandSideWidgets: (
    props: Required<PartialPropsOf<typeof StyledArticleLeftHandSideWidgets>, "topPicksWidgetProps">,
  ) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof StyledArticleLiveTag>) => ReactNode;
  moreOnThis: (props?: PartialPropsOf<typeof StyledArticleMoreOnThisList>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof StyledArticleSections>) => ReactNode;
  shareWidget: (props?: PartialPropsOf<typeof StyledArticleShareWidget>) => ReactNode;
  speechPlayer: (props: PartialPropsOf<typeof StyledArticleSpeechInlineWidget>) => ReactNode;
  speechPlayerPortal: (props: PartialPropsOf<typeof ArticleSpeechPlayerPortal>) => ReactNode;
  sponsor: (props?: PartialPropsOf<typeof StyledArticleSponsor>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof StyledContentSubHeadline>) => ReactNode;
  takeOverAdSlot: (props?: PartialPropsOf<typeof TakeoverAdSlot>) => ReactNode;
  topicFollowWidget: (props?: PartialPropsOf<typeof StyledArticleTopicFollowWidget>) => ReactNode;
  topics: (props?: PartialPropsOf<typeof StyledArticleTopics>) => ReactNode;
  vocabBox: (props?: PartialPropsOf<typeof StyledArticleVocabBox>) => ReactNode;
};

const Component: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleRenderArticle on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        entityId
        ...articleActionWidgetArticle
        ...articleAdditionalInfoBoxArticleContent
        ...articleAuthorBlockArticle
        ...articleAuthorDetailsContent
        ...articleBaseAdvertisingInfoArticle
        ...articleCommentsTriggerArticle
        ...articleCorrectionsWidgetArticle
        ...articleImageGalleryContent
        ...articleLeadingArticle
        ...articleLeftHandSideWidgetsContent
        ...articleMoreOnThisListArticle
        ...articlePaywallContainerArticle
        ...articlePrintHeadlineMessageArticle
        ...articlePrintHeadlineMessageArticle
        ...articleReactionArticle
        ...articleSectionsContent
        ...articleShareWidgetArticle
        ...articleSpeechInlineWidgetArticle
        ...articleSpeechPlayerPortalArticle
        ...articleSponsorContent
        ...articleTopicFollowWidgetArticle
        ...articleTopicsContent
        ...articleUtilsArticle
        ...articleVocabBoxArticle
        ...contentBodyContent @arguments(customContents: $customContents)
        ...contentHeadlineContent
        ...contentLiveTagContent
        ...contentReadMoreButtonContent
        ...contentSchemaRenderContent
        ...contentSubHeadlineContent
        ...contentSummaryContent
        ...genericArticleArticle
        ...globalCheckerContent
        ...globalCheckerContent
        ...helpersCheckIsPlusArticle
        ...helpersCheckIsPostiesArticle
        ...helpersCheckIsStyleArticle
        ...hooksPostiesArticleArticle @arguments(customContents: $customContents)
        ...infographicArticleArticle
        ...liveArticleArticle
        ...longReadArticleArticle
        ...morningStudioArticleContent
        ...newsletterArticleArticle
        ...opinionArticleContent
        ...photoEssayArticleArticle
        ...pianoIntegrationArticle
        ...plusGenericArticleContent
        ...plusColumnsArticleContent
        ...styleArticleContent
        ...seriesArticleArticle
      }
    `,
    reference_,
  );

  const advertising = useArticleBaseAdvertisingInfo(article);
  usePianoIntegration(article);
  const { getArticleType } = useArticleUtils();
  const articleType = getArticleType(article);

  const { paywallIsDomChangedMap, paywallMap } = usePaywallCheckerContext();
  const isShowInlinePaywall = useMemo(
    () => paywallMap?.[article.entityId],
    [article.entityId, paywallMap],
  );

  const isInlinePaywallDomChanged = useMemo(
    () => paywallIsDomChangedMap?.[article.entityId],
    [article.entityId, paywallIsDomChangedMap],
  );

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const styleInlineAdSlots = useMemo<InlineAdSlotRenderProps>(
    () =>
      isDesktopUp
        ? {
            style_article_0: {
              adUnit: "d_banner5",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], [300, 250], [1, 1], "fluid"],
            },
          }
        : {
            style_article_0: {
              adUnit: "m_banner5",
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [[320, 100], [300, 250], [1, 1], "fluid"],
            },
          },
    [isDesktopUp],
  );

  const { isReadMoreClicked, setIsReadMoreClicked } = useContentReadMoreContext();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const { isMostLikelyToSubscribeUser } = useMostLikelyToSubscribe();
  const provider: DefaultComponentsProvider = {
    actionWidget: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleActionWidget reference={article} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    adslot: props => (
      <ArticleSpeechSkipContainer>
        <StyledAdSlot isDisabled={isShowInlinePaywall === undefined} {...advertising} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    articleAdditionalInfoBox: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleAdditionalInfoBox contentReference={article} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    authorBlock: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleAuthorBlock reference={article} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    authorDetails: props => (
      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <ArticleSpeechSkipContainer>
            <StyledArticleAuthorsDetails reference={article} {...props} />
          </ArticleSpeechSkipContainer>
        </GlobalChecker>
      </ClientSideSuspense>
    ),
    body: props => {
      const { "0": firstAdSlot, ...inlineAdSlots } = Object.fromEntries(
        Object.entries(props?.inlineAdSlots ?? {}).map(([key, adSlot]) => [
          key,
          {
            ...adSlot,
            isDisabled: notEmpty(adSlot?.isDisabled) || isShowInlinePaywall === undefined,
          } as InlineAdSlotProps["slot"],
        ]),
      );
      const extraRenderFunctionMap = {
        ...props?.extraRenderFunctionMap,
      };

      let accumulateSkipCount = 0;
      let stylePrimaryContentPosition = 0;
      const findAdSlotPosition = (schema: Schema, position: number) =>
        schema.findIndex((node, index) => {
          const previousElementRow = position - 1;
          const lastChildren = (node?.children ?? []).slice(-1)?.[0];

          const isSkipCount =
            ["h1", "h2", "h3", "h4", "h5", "track-viewed-percentage", "inline-ad-slot"].includes(
              node.type,
            ) ||
            (["p", "div"].includes(node.type) &&
              lastChildren?.type === "iframe" &&
              lastChildren?.subtype === ("youtube" as SchemaSubtype));

          if (index >= previousElementRow + accumulateSkipCount && isSkipCount) {
            accumulateSkipCount++;
          }
          return index >= previousElementRow + accumulateSkipCount;
        });

      const primaryInlineAdSlots = checkIsStyleArticle(article)
        ? styleInlineAdSlots
        : {
            "0": firstAdSlot,
          };

      const handleRenderContentBody = () => (
        <>
          <PrimaryContentBody
            {...props}
            advertising={advertising}
            extraRenderFunctionMap={extraRenderFunctionMap}
            inlineAdSlots={primaryInlineAdSlots}
            preprocessSchema={schema => {
              if (articleType === articleData.types.live.entityId) return schema;

              if (checkIsStyleArticle(article)) {
                const position = findAdSlotPosition(schema, 4);
                const firstAdSchema = defineInlineAdSlotSchema({ row: 4, tag: "style_article_0" });

                const newSchema = [...schema];
                newSchema.splice(position + 1, 0, firstAdSchema);
                // for secondly content body use, start slice index count
                stylePrimaryContentPosition = position + 1;

                // plus one is include the first ads
                return newSchema.slice(0, position > -1 ? stylePrimaryContentPosition + 1 : 5);
              }

              const firstAdIndex = schema.findIndex(
                node => node.type === "inline-ad-slot" && node.attribs?.tag === "0",
              );

              const numberOfReadableElements = isMostLikelyToSubscribeUser
                ? 1
                : NumberOfReadableElementInPaywall;

              return isShowInlinePaywall
                ? schema.slice(0, numberOfReadableElements)
                : schema.slice(0, firstAdIndex > -1 ? firstAdIndex + 1 : 5);
            }}
            reference={article}
          >
            {isShowInlinePaywall && (
              <InlinePaywallOverlayer
                $height={isMostLikelyToSubscribeUser ? "small" : "normal"}
                $variant={props?.paywallVariant}
              />
            )}
            {/* Prevent show read more before paywall has been executed */}
            {isShowInlinePaywall === false && (
              <ContentReadMoreButtonContainer>
                <ArticleSpeechSkipContainer>
                  <ContentReadMoreButton reference={article} />
                </ArticleSpeechSkipContainer>
              </ContentReadMoreButtonContainer>
            )}
          </PrimaryContentBody>

          <ArticleSpeechSkipContainer
            breakpointString={
              isReadMoreClicked && isMobile
                ? theme.breakpoints.up("tablet")
                : theme.breakpoints.down("tablet")
            }
          >
            <SecondlyContentBody
              {...props}
              $isShow={isReadMoreClicked}
              advertising={advertising}
              extraRenderFunctionMap={extraRenderFunctionMap}
              inlineAdSlots={inlineAdSlots}
              preprocessSchema={schema => {
                if (checkIsStyleArticle(article)) {
                  schema = schema.slice(stylePrimaryContentPosition);
                  return props?.preprocessSchema ? props.preprocessSchema(schema) : schema;
                }

                if (isShowInlinePaywall === true || articleType === articleData.types.live.entityId)
                  return [];

                const firstAdIndex = schema.findIndex(
                  node => node.type === "inline-ad-slot" && node.attribs?.tag === "0",
                );
                const startFromIndex = firstAdIndex > -1 ? firstAdIndex + 1 : 5;
                schema = schema.slice(startFromIndex);
                return props?.preprocessSchema ? props.preprocessSchema(schema) : schema;
              }}
              reference={article}
            >
              <ArticlePrintHeadlineMessage
                reference={article}
                variant={props?.printHeadlineVariant}
              />
            </SecondlyContentBody>
            <StyledArticlePaywallContainer
              isShowInlinePaywall={isShowInlinePaywall}
              reference={article}
            />
          </ArticleSpeechSkipContainer>
        </>
      );

      if (props?.isDisabledArticleImageGallery) {
        return handleRenderContentBody();
      }

      return (
        <ArticleImageGallery reference={article}>{handleRenderContentBody()}</ArticleImageGallery>
      );
    },
    commentTriggerAndReaction: (props, reactionProps) => (
      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <ArticleSpeechSkipContainer>
            <StyledCommentAndReactionContainer>
              <StyledArticleCommentsTrigger
                location="Article Bottom"
                reference={article}
                variant="banner"
                {...props}
              />
              <StyledArticleReaction reference={article} {...reactionProps} />
            </StyledCommentAndReactionContainer>
          </ArticleSpeechSkipContainer>
        </GlobalChecker>
      </ClientSideSuspense>
    ),
    correction: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleCorrectionsWidget reference={article} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    headline: props => <StyledContentHeadline reference={article} {...props} />,
    leading: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleLeading
          responsiveVariants={{
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["768x768"],
            tabletUp: ImageSize["1020x680"],
          }}
          {...props}
          advertising={{
            ...advertising,
            ...props?.advertising,
          }}
          reference={article}
        />
      </ArticleSpeechSkipContainer>
    ),
    leftHandSideWidgets: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleLeftHandSideWidgets reference={article} {...props} />{" "}
      </ArticleSpeechSkipContainer>
    ),
    liveTag: props => <StyledArticleLiveTag reference={article} {...props} />,
    moreOnThis: props => (
      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <ArticleSpeechSkipContainer>
            <StyledArticleMoreOnThisList reference={article} {...props} />
          </ArticleSpeechSkipContainer>
        </GlobalChecker>
      </ClientSideSuspense>
    ),
    sections: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleSections reference={article} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    shareWidget: props => {
      const widget = (
        <ArticleSpeechSkipContainer>
          <StyledArticleShareWidget reference={article} {...props} />
        </ArticleSpeechSkipContainer>
      );
      switch (props?.variant) {
        case "longRead":
        case "longReadMobile":
        case "photoEssay":
        case "photoEssayMobile":
          return (
            <ClientSideSuspense>
              <GlobalChecker reference={article}>{widget}</GlobalChecker>
            </ClientSideSuspense>
          );
        default:
          return widget;
      }
    },
    speechPlayer: props => (
      <StyledArticleSpeechInlineWidget
        reference={article}
        variant="generic"
        onClick={() => {
          setIsReadMoreClicked?.(true);
        }}
        {...props}
      />
    ),
    speechPlayerPortal: props => (
      <ArticleSpeechPlayerPortal
        reference={article}
        variant="generic-minify"
        onClick={() => {
          setIsReadMoreClicked?.(true);
        }}
        {...props}
      />
    ),
    sponsor: props => <StyledArticleSponsor reference={article} {...props} />,
    subHeadline: props =>
      !(isShowInlinePaywall === true && isMostLikelyToSubscribeUser) && (
        <StyledContentSubHeadline reference={article} {...props} />
      ),
    takeOverAdSlot: props => <TakeoverAdSlot adSlotProps={advertising} {...props} />,
    topicFollowWidget: props => (
      <ArticleSpeechSkipContainer>
        <StyledArticleTopicFollowWidget reference={article} {...props} />
      </ArticleSpeechSkipContainer>
    ),
    topics: props => (
      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <ArticleSpeechSkipContainer>
            <StyledArticleTopics reference={article} {...props} />
          </ArticleSpeechSkipContainer>
        </GlobalChecker>
      </ClientSideSuspense>
    ),
    vocabBox: props => <StyledArticleVocabBox reference={article} {...props} />,
  };

  const isPostiesArticle = checkIsPostiesArticle(article);
  const { handleRenderPostiesArticle } = usePostiesArticle(provider, article);

  const handleRenderApplicationArticle = () => {
    if (isPostiesArticle) {
      return handleRenderPostiesArticle();
    }

    if (checkIsStyleArticle(article)) {
      return <DynamicStyleArticle provider={provider} reference={article} />;
    }

    if (checkIsPlusArticle(article)) {
      switch (articleType) {
        case articleData.types.opinion.entityId:
        case articleData.types.column.entityId:
          return <DynamicPlusColumnsArticle provider={provider} reference={article} />;
        default:
          return <DynamicPlusGenericArticle provider={provider} reference={article} />;
      }
    }

    return handleRenderArticle();
  };

  const handleRenderArticle = () => {
    switch (articleType) {
      case articleData.types.opinion.entityId:
      case articleData.types.column.entityId:
        return (
          <DynamicOpinionArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case articleData.types.newsletter.entityId:
        return <DynamicNewsletterArticle provider={provider} reference={article} />;
      case articleData.types.studio.entityId:
        return <DynamicMorningStudioArticle provider={provider} reference={article} />;
      case articleData.types.longRead.entityId:
        return (
          <DynamicLongReadArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case articleData.types.photoEssay.entityId:
        return (
          <DynamicPhotoEssayArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case articleData.types.live.entityId:
        return (
          <DynamicLiveArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );

      case articleData.types.infographic.entityId:
      case articleData.types.infographicLongRead.entityId:
        return (
          <DynamicInfographicArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case articleData.types.series.entityId:
        return <DynamicSeriesArticle provider={provider} reference={article} />;
      default:
        return (
          <DynamicGenericArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
    }
  };

  const { articleElementReference } = useArticleRenderContext();

  return (
    <ArticleRenderContainer ref={articleElementReference}>
      <ThemeProvider theme={defaultTheme}>{handleRenderApplicationArticle()}</ThemeProvider>
    </ArticleRenderContainer>
  );
};

Component.displayName = "ArticleRender";
export const ArticleRender = withArticleRenderContext(Component);
